import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, BehaviorSubject, tap, catchError, throwError, interval, switchMap, of } from 'rxjs';
import { Router } from '@angular/router';
import { WtStorageService } from 'src/app/wt-storage.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';

// DTOs matching backend
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: UserInfo;
}

export interface UserInfo {
  userId: string;
  email: string;
  organizationId: number;
  userRole: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

@Injectable()
export class AuthService {
  private loggedOutCookieName: string = 'l_o';
  public isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();
  private liveEventPromptCookieName = 'liveEventPrompt';

  private readonly apiUrl = environment.apiUrl || '/api';
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly TOKEN_EXPIRY_KEY = 'token_expiry';

  private refreshTokenTimer: any;

  constructor(
    private http: HttpClient,
    private router: Router,
    private wtStorage: WtStorageService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  public init() {
    if (isPlatformBrowser(this.platformId)) {
      // Check if user has valid token on init
      const token = this.getAccessToken();
      if (token && !this.isTokenExpired()) {
        this.isAuthenticatedSubject$.next(true);
        this.scheduleTokenRefresh();
      } else if (token && this.isTokenExpired()) {
        // Try to refresh if token is expired
        this.refreshAccessToken().subscribe({
          next: () => {
            this.isAuthenticatedSubject$.next(true);
          },
          error: () => {
            this.logOut();
          }
        });
      }

      // Listen for storage changes (logout in other tabs)
      window.addEventListener('storage', ({ key, oldValue, newValue }) => {
        if (key === this.ACCESS_TOKEN_KEY && !newValue) {
          this.logOut();
        }
      });
    }
  }

  public login(email: string, password: string): Observable<LoginResponse> {
    const request: LoginRequest = { email, password };

    return this.http.post<LoginResponse>(`${this.apiUrl}/Auth/login`, request).pipe(
      tap(response => {
        this.handleLoginSuccess(response);
      }),
      catchError(this.handleError)
    );
  }

  private handleLoginSuccess(response: LoginResponse): void {
    // Store tokens
    this.wtStorage.setItem(this.ACCESS_TOKEN_KEY, response.accessToken);
    this.wtStorage.setItem(this.REFRESH_TOKEN_KEY, response.refreshToken);

    // Calculate and store expiry time
    const expiryTime = new Date().getTime() + (response.expiresIn * 1000);
    this.wtStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());

    // Store user info in the existing format for backward compatibility
    const userProfile = {
      userId: response.user.userId,
      email: response.user.email,
      organizationId: response.user.organizationId,
      userRole: response.user.userRole
    };
    this.wtStorage.setuser(userProfile as any);

    // Update authentication state
    this.isAuthenticatedSubject$.next(true);

    // Schedule token refresh
    this.scheduleTokenRefresh();
  }

  public refreshAccessToken(): Observable<LoginResponse> {
    const refreshToken = this.wtStorage.getItem(this.REFRESH_TOKEN_KEY);

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const request: RefreshTokenRequest = { refreshToken };

    return this.http.post<LoginResponse>(`${this.apiUrl}/Auth/refresh`, request).pipe(
      tap(response => {
        this.handleLoginSuccess(response);
      }),
      catchError(error => {
        this.logOut();
        return throwError(() => error);
      })
    );
  }

  private scheduleTokenRefresh(): void {
    // Clear existing timer
    if (this.refreshTokenTimer) {
      clearTimeout(this.refreshTokenTimer);
    }

    const expiryTime = parseInt(this.wtStorage.getItem(this.TOKEN_EXPIRY_KEY) || '0');
    const currentTime = new Date().getTime();
    const timeUntilExpiry = expiryTime - currentTime;

    // Refresh 1 minute before expiry
    const refreshTime = timeUntilExpiry - (60 * 1000);

    if (refreshTime > 0) {
      this.refreshTokenTimer = setTimeout(() => {
        this.refreshAccessToken().subscribe({
          error: () => this.logOut()
        });
      }, refreshTime);
    }
  }

  public isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired();
  }

  private isTokenExpired(): boolean {
    const expiryTime = parseInt(this.wtStorage.getItem(this.TOKEN_EXPIRY_KEY) || '0');
    return new Date().getTime() >= expiryTime;
  }

  public logOut(): void {
    const refreshToken = this.wtStorage.getItem(this.REFRESH_TOKEN_KEY);

    // Call logout endpoint to revoke tokens
    if (refreshToken) {
      this.http.post(`${this.apiUrl}/Auth/logout`, { refreshToken }).subscribe({
        complete: () => this.clearAuthData()
      });
    } else {
      this.clearAuthData();
    }
  }

  private clearAuthData(): void {
    // Clear tokens
    this.wtStorage.removeItem(this.ACCESS_TOKEN_KEY);
    this.wtStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this.wtStorage.removeItem(this.TOKEN_EXPIRY_KEY);

    // Clear cookies
    deleteCookie(this.loggedOutCookieName);
    deleteCookie(this.liveEventPromptCookieName);

    // Clear storage
    this.wtStorage.clear();

    // Clear refresh timer
    if (this.refreshTokenTimer) {
      clearTimeout(this.refreshTokenTimer);
    }

    // Update authentication state
    this.isAuthenticatedSubject$.next(false);

    // Navigate to login
    void this.router.navigate(['/login']);
  }

  public getAccessToken(): string {
    return this.wtStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  public get user(): UserInfo | null {
    const userProfile = this.wtStorage.getuser();
    if (userProfile) {
      return {
        userId: userProfile.id?.toString() || '',
        email: userProfile.userName || '',
        organizationId: userProfile.organizationId || 0,
        userRole: userProfile.userRole?.toString() || ''
      };
    }
    return null;
  }

  public getIdentityClaims(): any {
    return this.user;
  }

  public isLoggedout(): boolean {
    const lgcookie = this.getCookie(this.loggedOutCookieName);
    return !!lgcookie;
  }

  public getLoggedOutCookie(): string {
    return this.getCookie('l_o');
  }

  // Given a cookie key `name`, returns the value of
  // the cookie or `null`, if the key is not found.
  private getCookie(name: string): string {
    const nameLenPlus = name.length + 1;
    return (
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .filter((cookie) => {
          return cookie.substring(0, nameLenPlus) === `${name}=`;
        })
        .map((cookie) => {
          return decodeURIComponent(cookie.substring(nameLenPlus));
        })[0] || null
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred during authentication';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = error.error?.message || error.message || errorMessage;
    }

    return throwError(() => new Error(errorMessage));
  }
}

export function deleteCookie(name: string) {
  const date = new Date();

  // Set it expire in -1 days
  date.setTime(date.getTime() + -1 * 24 * 60 * 60 * 1000);

  // Set it
  document.cookie = name + '=; expires=' + date.toUTCString() + '; path=/';
}
